/**
 * ConsentScreen (S0b) — one-time biometric consent.
 * Props: { ctx, api, tg, nav, state, setState, params } (see App.jsx).
 *
 *   - MainButton "Roziman, davom etish" → api.consent().
 *   - on ok: setState({session:{...session, consent:true}}) then route
 *       ctx.enrolled ? nav.replace(LANDING) : nav.replace(ENROLL).
 *   - secondary "Rad etish" → tg.close() (leaves the Mini App).
 */
import { useEffect, useState } from 'react';
import Screen from '../components/Screen.jsx';
import Button from '../components/Button.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { T } from '../lib/i18n.js';
import { SCREENS } from '../App.jsx';

export default function ConsentScreen({ ctx, api, tg, nav, state, setState }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function accept() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    tg.MainButton.showProgress();
    const res = await api.consent();
    tg.MainButton.hideProgress();
    setBusy(false);
    if (res && res.ok) {
      tg.haptic('success');
      setState({ session: { ...(state.session || {}), consent: true } });
      nav.replace(ctx.enrolled ? SCREENS.LANDING : SCREENS.ENROLL);
    } else {
      tg.haptic('error');
      setErr((res && res.error) || 'SERVER_ERROR');
    }
  }

  function decline() {
    tg.haptic('warning');
    tg.close();
  }

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    return tg.MainButton.onClick(accept);
  });

  useEffect(() => {
    if (!tg.isTelegram) return undefined;
    tg.MainButton.setText(T.btn_consent_accept);
    tg.MainButton.show();
    return () => tg.MainButton.hide();
  }, [tg]);

  if (err) {
    return (
      <Screen title={T.title_consent}>
        <ErrorState code={err} onRecover={() => setErr(null)} recover="retry" />
      </Screen>
    );
  }

  return (
    <Screen
      title={T.title_consent}
      bodyClass="consent"
      footer={
        <>
          {!tg.isTelegram ? (
            <Button onClick={accept} disabled={busy}>
              {T.btn_consent_accept}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={decline}>
            {T.btn_consent_decline}
          </Button>
        </>
      }
    >
      <div className="cn-ic">🪪</div>
      <div className="cn-h">{T.consent_h}</div>
      <div className="cn-b">{T.consent_b}</div>
      <div className="cn-pills">
        <span>{T.consent_pill_only}</span>
        <span>{T.consent_pill_enc}</span>
        <span>{T.consent_pill_third}</span>
      </div>
    </Screen>
  );
}
